/**
 * One question of the card: a fieldset whose legend is the question, and its
 * rows — the options, then «Не знаю — подскажите» and «Свой ответ…» when the
 * question allows them (facet and R1 rounds do not).
 *
 * Single-select is a radiogroup with a roving tab stop: Tab lands on the
 * chosen row (or the first); Arrow keys, Home and End move focus only. Space,
 * Enter or a tap chooses — and a choice advances the card, which is exactly why
 * selection must not follow focus here. Multi-select rows are checkboxes, each
 * its own tab stop.
 */
import { PenLine } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { iconProps } from "@/lib/icons";

import { ClarifyCustomAnswer } from "./ClarifyCustomAnswer";
import { ClarifyOptionRow } from "./ClarifyOptionRow";
import { buildChoices, isChoiceChecked, type ClarifyChoice, type QuestionDraft } from "./draft";
import type { ClarifySkin } from "./skin";
import type { ClarifyRoundQuestion } from "./types";

export interface ClarifyQuestionViewProps {
  question: ClarifyRoundQuestion;
  answer: QuestionDraft;
  legendId: string;
  skin: ClarifySkin;
  disabled: boolean;
  /** Focus the chosen (or first) row on mount. */
  focusOnMount: boolean;
  customMaxChars: number;
  onChoose: (choice: ClarifyChoice) => void;
  onCustomText: (text: string) => void;
  onCustomConfirm: () => void;
}

function choiceTestId(choice: ClarifyChoice): string {
  if (choice.kind === "option") return `clarify-option-${choice.optionId}`;
  return choice.kind === "dontKnow" ? "clarify-dont-know" : "clarify-custom";
}

export function ClarifyQuestionView({
  question,
  answer,
  legendId,
  skin,
  disabled,
  focusOnMount,
  customMaxChars,
  onChoose,
  onCustomText,
  onCustomConfirm,
}: ClarifyQuestionViewProps): ReactElement {
  const { t } = useTranslation("chat");
  const choices = useMemo(() => buildChoices(question), [question]);
  const checkedIndex = choices.findIndex((choice) => isChoiceChecked(answer, choice));
  const [roving, setRoving] = useState(Math.max(0, checkedIndex));
  const [customFocus, setCustomFocus] = useState(false);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Read once: the card decides at mount whether this question takes focus.
  const initialFocus = useRef(focusOnMount ? Math.max(0, checkedIndex) : -1);

  useEffect(() => {
    const target = initialFocus.current;
    if (target >= 0) rowRefs.current[target]?.focus({ preventScroll: true });
  }, []);

  const single = !question.multi;
  const optionLabels = new Map(question.options.map((option) => [option.id, option]));

  const focusRow = (index: number): void => {
    const count = choices.length;
    rowRefs.current[((index % count) + count) % count]?.focus();
  };

  const onRowKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    if (!single) return;
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        focusRow(index + 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        focusRow(index - 1);
        break;
      case "Home":
        event.preventDefault();
        focusRow(0);
        break;
      case "End":
        event.preventDefault();
        focusRow(choices.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <fieldset className="m-0 min-w-0 border-0 p-0" data-testid="clarify-question" data-question-id={question.id}>
      <legend id={legendId} data-testid="clarify-legend" className={skin.legend}>
        {question.text}
      </legend>
      <div role={single ? "radiogroup" : "group"} aria-labelledby={legendId} className="flex flex-col gap-2">
        {choices.map((choice, index) => {
          const option = choice.kind === "option" ? optionLabels.get(choice.optionId) : undefined;
          const label =
            choice.kind === "option"
              ? (option?.label ?? "")
              : choice.kind === "dontKnow"
                ? t("clarify.dontKnow")
                : t("clarify.custom");
          return (
            <ClarifyOptionRow
              key={choiceTestId(choice)}
              role={single ? "radio" : "checkbox"}
              checked={isChoiceChecked(answer, choice)}
              label={label}
              desc={option?.desc ?? null}
              recommended={option?.rec === true}
              leading={choice.kind === "custom" ? <PenLine {...iconProps("sm")} /> : null}
              testId={choiceTestId(choice)}
              tabIndex={single ? (index === roving ? 0 : -1) : 0}
              disabled={disabled}
              skin={skin}
              buttonRef={(element) => {
                rowRefs.current[index] = element;
              }}
              onActivate={() => {
                // The chosen row is the tab stop even where a click does not
                // focus a button (Safari), so Tab returns to the choice.
                setRoving(index);
                if (choice.kind === "custom") setCustomFocus(true);
                onChoose(choice);
              }}
              onFocus={() => setRoving(index)}
              onKeyDown={(event) => onRowKeyDown(event, index)}
            />
          );
        })}
      </div>
      {answer.custom !== null ? (
        <ClarifyCustomAnswer
          value={answer.custom}
          max={customMaxChars}
          autoFocus={customFocus}
          skin={skin}
          onChange={onCustomText}
          onConfirm={onCustomConfirm}
        />
      ) : null}
    </fieldset>
  );
}
